/* RestForward motion: scroll reveals, hero drift, marquee pause, step sequencing.
 * No dependencies. Only toggles classes and a transform custom property, so the
 * browser animates transform/opacity on the compositor and layout never shifts.
 * html.restforward-motion is set inline in theme.liquid only when motion is allowed. */
const root = document.documentElement;
const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

function motionOn() {
  return root.classList.contains('restforward-motion') && !reduce.matches;
}

/* ---------------------------------------------------------- scroll reveal */
let revealObserver;

function reveal(el) {
  el.classList.add('is-in');
}

/* Native Horizon grids (collection, search, recommendations, upsells) join the same
   reveal system. Section re-renders drop these client-only attributes, which simply
   leaves the fresh markup visible. */
const AUTO_REVEAL = '.product-grid__item, .resource-list__item, .blog-post-card, .collection-card';

function tagNative(scope) {
  scope.querySelectorAll(AUTO_REVEAL).forEach((el) => {
    if (el.hasAttribute('data-reveal') || el.closest('.restforward, [data-reveal]')) return;
    const siblings = el.parentElement ? [...el.parentElement.children] : [el];
    el.setAttribute('data-reveal', '');
    el.style.setProperty('--reveal-i', String(Math.min(siblings.indexOf(el), 5)));
  });
}

function initReveals(scope = document) {
  if (motionOn()) tagNative(scope);
  const items = scope.querySelectorAll('[data-reveal]:not(.is-in)');
  if (!items.length) return;

  // Stagger children inside a [data-reveal-group] by their order.
  scope.querySelectorAll('[data-reveal-group]').forEach((group) => {
    group.querySelectorAll('[data-reveal]').forEach((el, i) => el.style.setProperty('--reveal-i', String(i)));
  });

  if (!motionOn() || !('IntersectionObserver' in window)) {
    items.forEach(reveal);
    return;
  }

  revealObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        reveal(entry.target);
        revealObserver.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  items.forEach((el) => revealObserver.observe(el));
}

/* ------------------------------------------------ in-view state (sections) */
let viewObserver;

function syncHeroVideo(section, inView) {
  const video = section.querySelector('.restforward-hero__video');
  if (!video) return;
  if (!motionOn() || !inView) {
    video.pause();
    return;
  }
  video.play().catch(() => {});
}

function initInView(scope = document) {
  const items = scope.querySelectorAll('[data-restforward-inview]');
  if (!items.length) return;
  if (!('IntersectionObserver' in window)) {
    items.forEach((el) => {
      el.classList.add('is-inview');
      syncHeroVideo(el, true);
    });
    return;
  }
  viewObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        entry.target.classList.toggle('is-inview', entry.isIntersecting);
        syncHeroVideo(entry.target, entry.isIntersecting);
      }
    },
    { threshold: 0.2 }
  );
  items.forEach((el) => viewObserver.observe(el));
}

/* ------------------------------------------------------- hero parallax drift */
const drifters = new Set();
let ticking = false;

function updateDrift() {
  ticking = false;
  const vh = window.innerHeight;
  for (const el of drifters) {
    const rect = el.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > vh) continue;
    const strength = Number(el.dataset.restforwardDrift) || 0.08;
    // 0 while the element sits at the top of the viewport; moves slower than the scroll.
    const offset = Math.max(-80, Math.min(80, -rect.top * strength));
    el.style.setProperty('--restforward-drift', `${offset.toFixed(1)}px`);
  }
}

function onScroll() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(updateDrift);
}

function initDrift(scope = document) {
  if (!motionOn()) return;
  scope.querySelectorAll('[data-restforward-drift]').forEach((el) => drifters.add(el));
  if (!drifters.size) return;
  window.addEventListener('scroll', onScroll, { passive: true });
  updateDrift();
}

/* ------------------------------------------------------------- marquee pause */
function initMarquees(scope = document) {
  scope.querySelectorAll('[data-restforward-marquee]').forEach((marquee) => {
    const button = marquee.querySelector('[data-restforward-marquee-toggle]');
    if (!button || button.dataset.bound) return;
    button.dataset.bound = 'true';
    button.addEventListener('click', () => {
      const paused = marquee.toggleAttribute('data-paused');
      button.setAttribute('aria-pressed', String(paused));
      const label = paused ? button.dataset.labelPlay : button.dataset.labelPause;
      if (label) button.setAttribute('aria-label', label);
    });
  });
}

function init(scope = document) {
  initReveals(scope);
  initInView(scope);
  initDrift(scope);
  initMarquees(scope);
}

init();
window.__restforwardMotionReady = true;

// If the visitor switches reduced motion on mid-visit, show everything at once.
reduce.addEventListener?.('change', () => {
  if (!reduce.matches) return;
  root.classList.remove('restforward-motion');
  document.querySelectorAll('[data-reveal]').forEach(reveal);
  document.querySelectorAll('.restforward-hero__video').forEach((video) => video.pause());
});

// Theme editor: sections are re-rendered in place.
document.addEventListener('shopify:section:load', (event) => init(event.target));
